/**
 * Which chapter openers have an animated explainer, keyed by opener slug.
 *
 * A registry in code rather than a key in the opener's frontmatter, because an
 * explainer *is* code — a set of drawn scenes — and there is nothing for the
 * build to carry: `scripts/build-openers.mjs` ships the essay, and the page
 * asks this table whether there is also a picture of it. Each entry is a lazy
 * import, so a reader of an opener without one downloads nothing
 * extra, and a reader of this one fetches the scenes once the essay is up.
 *
 * `web/test/explainer.test.ts` holds every key here to a real opener slug.
 */
import type { ComponentType } from 'react';
import type { ExplainerChapter } from './Player';

type Loader = () => Promise<{ default: ComponentType<{ chapter?: ExplainerChapter }> }>;

export const EXPLAINERS: Readonly<Record<string, Loader>> = {
  'the-workstation': () => import('./the-workstation'),
  'first-commits': () => import('./first-commits'),
  'branches-remotes': () => import('./branches-remotes'),
  recovery: () => import('./recovery'),
  quality: () => import('./quality'),
  'team-git': () => import('./team-git'),
  'code-review': () => import('./code-review'),
  'the-codebase': () => import('./the-codebase'),
  releases: () => import('./releases'),
  production: () => import('./production'),
  'the-pipeline': () => import('./the-pipeline'),
  data: () => import('./data'),
  design: () => import('./design'),
  ownership: () => import('./ownership'),
  'the-platform': () => import('./the-platform'),
  'the-program': () => import('./the-program'),
  'the-network': () => import('./the-network'),
  'the-database': () => import('./the-database'),
  'the-api': () => import('./the-api'),
  security: () => import('./security'),
  concurrency: () => import('./concurrency'),
  observability: () => import('./observability'),
  'scale-and-cost': () => import('./scale-and-cost'),
};

/**
 * Explainers that belong to a page rather than to a chapter opener, keyed by
 * the slug their recordings live under. The landing page has two: the
 * swimmer and the ship (why learn engineering at all), then the course pitch
 * (why learn it here). They have no essay, so it cannot be in the table above (which the tests hold
 * to missions/_worlds), and it is lazy for the same reason — a visitor who
 * never presses Play downloads none of its scenes.
 */
export const PAGE_EXPLAINERS = {
  'swimmer-and-ship': () => import('./swimmer-and-ship'),
  'why-engineering': () => import('./why-engineering'),
} as const satisfies Readonly<Record<string, Loader>>;
